import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { emailTemplateApi, EmailTemplate, EmailTemplateType, CreateEmailTemplateRequest, UpdateEmailTemplateRequest } from '../services/api';
import ConfirmationModal from './ConfirmationModal';
import { useConfirmationModal, modalPresets } from '../hooks/useConfirmationModal';

const AdminEmailTemplates: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [previewVariables, setPreviewVariables] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);

  const queryClient = useQueryClient();
  
  // Confirmation modal hook
  const { isOpen, modalConfig, showModal, confirm, cancel } = useConfirmationModal();

  // Fetch all templates
  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: async () => {
      const response = await emailTemplateApi.getAll();
      return response.data;
    },
  });

  // Fetch template stats
  const { data: stats } = useQuery({
    queryKey: ['emailTemplateStats'],
    queryFn: async () => {
      const response = await emailTemplateApi.getStats();
      return response.data;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: CreateEmailTemplateRequest) => {
      console.log('Creating email template with data:', data);
      try {
        const response = await emailTemplateApi.create(data);
        console.log('API call successful, response:', response);
        return response;
      } catch (error: any) {
        console.error('API call failed:', error);
        console.error('Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          config: error.config
        });
        throw error;
      }
    },
    onSuccess: (response) => {
      console.log('Email template created successfully:', response.data);
      toast.success('قالب ایمیل با موفقیت ایجاد شد!', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['emailTemplateStats'] });
      setIsCreating(false);
      setSelectedTemplate(null);
    },
    onError: (error: any) => {
      console.error('Error creating template:', error);
      const errorMessage = error.response?.data?.message || error.message || 'خطا در ایجاد قالب ایمیل';
      toast.error(errorMessage, {
        style: { direction: 'rtl', textAlign: 'right' }
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEmailTemplateRequest }) =>
      emailTemplateApi.update(id, data),
    onSuccess: () => {
      toast.success('قالب ایمیل با موفقیت به‌روزرسانی شد!', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      setIsEditing(false);
      setSelectedTemplate(null);
    },
    onError: (error: any) => {
      console.error('Error updating template:', error);
      toast.error('خطا در به‌روزرسانی قالب ایمیل', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => emailTemplateApi.delete(id),
    onSuccess: () => {
      toast.success('قالب ایمیل با موفقیت حذف شد!', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['emailTemplateStats'] });
      setSelectedTemplate(null);
    },
    onError: (error: any) => {
      console.error('Error deleting template:', error);
      toast.error('خطا در حذف قالب ایمیل', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
    },
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => emailTemplateApi.setAsDefault(id),
    onSuccess: () => {
      toast.success('قالب پیش‌فرض با موفقیت به‌روزرسانی شد!', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
    onError: (error: any) => {
      console.error('Error setting default template:', error);
      toast.error('خطا در تنظیم قالب پیش‌فرض', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => emailTemplateApi.toggleActive(id),
    onSuccess: () => {
      toast.success('وضعیت قالب با موفقیت به‌روزرسانی شد!', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
    },
    onError: (error: any) => {
      console.error('Error toggling template status:', error);
      toast.error('خطا در به‌روزرسانی وضعیت قالب', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
    },
  });

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: ({ id, variables }: { id: string; variables: Record<string, string> }) => 
      emailTemplateApi.preview(id, variables),
    onSuccess: (response) => {
      const preview = response.data;
      // Open preview in new window
      const previewWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
      if (previewWindow) {
        previewWindow.document.write(`
          <html dir="rtl" lang="fa">
            <head>
              <title>پیش‌نمایش ایمیل - ${preview.subject}</title>
              <meta charset="UTF-8">
              <style>
                body { margin: 0; padding: 20px; font-family: Arial, sans-serif; background-color: #f5f5f5; direction: rtl; }
                .preview-container { background: white; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); overflow: hidden; }
                .preview-header { background: #667eea; color: white; padding: 20px; text-align: center; }
                .preview-content { padding: 0; }
                iframe { width: 100%; border: none; min-height: 500px; }
              </style>
            </head>
            <body>
              <div class="preview-container">
                <div class="preview-header">
                  <h2>پیش‌نمایش ایمیل</h2>
                  <p><strong>موضوع:</strong> ${preview.subject}</p>
                </div>
                <div class="preview-content">
                  <iframe srcdoc="${preview.htmlBody.replace(/"/g, '&quot;')}"></iframe>
                </div>
              </div>
            </body>
          </html>
        `);
        previewWindow.document.close();
      }
    },
    onError: (error: any) => {
      console.error('Error previewing template:', error);
      toast.error('خطا در تولید پیش‌نمایش', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
    },
  });

  const getTemplateTypeLabel = (type: EmailTemplateType): string => {
    // Convert numeric type to enum if needed
    const numericType = typeof type === 'number' ? type : Number(type);
    
    const typeLabels: Record<number, string> = {
      1: 'ثبت‌نام',
      2: 'تایید ایمیل', 
      3: 'بازیابی رمز عبور',
      4: 'خوش‌آمدگویی',
      5: 'خبرنامه',
      6: 'آگهی تایید شد',
      7: 'آگهی رد شد',
      8: 'آگهی در حال انقضا',
      9: 'کسب‌وکار تایید شد',
      10: 'کسب‌وکار رد شد',
      11: 'تایید پرداخت',
      12: 'پیشنهاد فعال شد',
      13: 'اعلان سیستم',
      14: 'تبلیغات',
      15: 'فرم تماس',
    };
    
    return typeLabels[numericType] || 'نامشخص';
  };

  const handlePreview = (template: EmailTemplate) => {
    // Default variables for preview
    const defaultVariables: Record<string, string> = {
      user_name: 'John Doe',
      user_email: 'john.doe@example.com',
      site_name: 'timcheh',
      site_url: 'https://timcheh.ca',
      current_date: new Date().toLocaleDateString(),
      current_year: new Date().getFullYear().toString(),
      verification_code: '123456',
      activation_url: 'https://timcheh.ca/activate-email?token=sample-token',
    };

    previewMutation.mutate({
      id: template.id,
      variables: defaultVariables,
    });
  };

  const handleCreateTemplate = (formData: FormData) => {
    try {
      const name = (formData.get('name') as string)?.trim() || '';
      const subject = (formData.get('subject') as string)?.trim() || '';
      const htmlBody = (formData.get('htmlBody') as string)?.trim() || '';
      const typeValue = (formData.get('type') as string)?.trim() || '';
      
      console.log('Form values:', { name, subject, htmlBody, typeValue });
      
      if (!name || !subject || !htmlBody || !typeValue) {
        console.error('Validation failed - missing required fields:', { name: !!name, subject: !!subject, htmlBody: !!htmlBody, typeValue: !!typeValue });
        toast.error('لطفاً تمام فیلدهای الزامی را پر کنید', {
          style: { direction: 'rtl', textAlign: 'right' }
        });
        return;
      }

      const parsedType = parseInt(typeValue);
      if (isNaN(parsedType)) {
        console.error('Invalid type value:', typeValue);
        toast.error('نوع قالب نامعتبر است', {
          style: { direction: 'rtl', textAlign: 'right' }
        });
        return;
      }

      const data: CreateEmailTemplateRequest = {
        name: name,
        subject: subject,
        htmlBody: htmlBody,
        textBody: (formData.get('textBody') as string)?.trim() || undefined,
        type: parsedType as EmailTemplateType,
        isActive: formData.get('isActive') === 'true',
        isDefault: formData.get('isDefault') === 'true',
        description: (formData.get('description') as string)?.trim() || undefined,
      };
      
      console.log('Calling createMutation with data:', data);
      createMutation.mutate(data);
    } catch (error) {
      console.error('Error processing form data:', error);
      toast.error('خطا در پردازش داده‌های فرم. لطفاً دوباره تلاش کنید.', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
    }
  };

  const handleUpdateTemplate = (formData: FormData) => {
    if (!selectedTemplate) return;
    
    const data: UpdateEmailTemplateRequest = {
      name: formData.get('name') as string,
      subject: formData.get('subject') as string,
      htmlBody: formData.get('htmlBody') as string,
      textBody: formData.get('textBody') as string || undefined,
      isActive: formData.get('isActive') === 'true',
      description: formData.get('description') as string || undefined,
    };
    updateMutation.mutate({ id: selectedTemplate.id, data });
  };

  const handleDeleteTemplate = (id: string) => {
    showModal({
      ...modalPresets.deleteEn('this email template', () => {
        deleteMutation.mutate(id);
      }),
      isRtl: true
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-center py-8" dir="rtl">
        خطا در بارگذاری قالب‌های ایمیل. لطفاً دوباره تلاش کنید.
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">قالب‌های ایمیل</h2>
          <p className="text-gray-600">مدیریت قالب‌های ایمیل برای اهداف مختلف</p>
        </div>
        <button
          onClick={() => {
            setSelectedTemplate(null);
            setIsCreating(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          ایجاد قالب
        </button>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-semibold text-blue-600">{stats.totalTemplates}</div>
            <div className="text-blue-800">کل قالب‌ها</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-semibold text-green-600">{stats.activeTemplates}</div>
            <div className="text-green-800">قالب‌های فعال</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-semibold text-red-600">{stats.inactiveTemplates}</div>
            <div className="text-red-800">قالب‌های غیرفعال</div>
          </div>
        </div>
      )}

      {/* Templates List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">قالب‌های ایمیل</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  نام
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  نوع
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  وضعیت
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  پیش‌فرض
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  تاریخ ایجاد
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  عملیات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates?.map((template) => (
                <tr key={template.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{template.name}</div>
                    <div className="text-sm text-gray-500">{template.subject}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {template.typeName}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      template.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {template.isActive ? 'فعال' : 'غیرفعال'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {template.isDefault && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        پیش‌فرض
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(template.createdAt).toLocaleDateString('fa-IR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium space-x-reverse space-x-2">
                    <button
                      onClick={() => handlePreview(template)}
                      className="text-purple-600 hover:text-purple-900"
                      title="پیش‌نمایش قالب"
                    >
                      پیش‌نمایش
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTemplate(template);
                        setIsEditing(true);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      ویرایش
                    </button>
                    {!template.isDefault && (
                      <button
                        onClick={() => setDefaultMutation.mutate(template.id)}
                        className="text-green-600 hover:text-green-900"
                      >
                        تنظیم به عنوان پیش‌فرض
                      </button>
                    )}
                    <button
                      onClick={() => toggleActiveMutation.mutate(template.id)}
                      className="text-yellow-600 hover:text-yellow-900"
                    >
                      {template.isActive ? 'غیرفعال کردن' : 'فعال کردن'}
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      حذف
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(isCreating || isEditing) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {isCreating ? 'ایجاد قالب ایمیل' : 'ویرایش قالب ایمیل'}
              </h3>
            </div>
            
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Form submitted, isCreating:', isCreating, 'isEditing:', isEditing);
                const formData = new FormData(e.currentTarget);
                console.log('FormData entries:', Array.from(formData.entries()));
                if (isCreating) {
                  console.log('Calling handleCreateTemplate');
                  handleCreateTemplate(formData);
                } else if (isEditing) {
                  console.log('Calling handleUpdateTemplate');
                  handleUpdateTemplate(formData);
                } else {
                  console.warn('Form submitted but neither creating nor editing');
                }
              }}
              className="p-6 space-y-4"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    نام قالب
                  </label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={selectedTemplate?.name || ''}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    نوع قالب
                  </label>
                  <select
                    name="type"
                    defaultValue={selectedTemplate?.type || EmailTemplateType.Registration}
                    required
                    disabled={isEditing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    {Object.entries(EmailTemplateType)
                      .filter(([key, value]) => typeof value === 'number')
                      .map(([key, value]) => (
                        <option key={value} value={value}>
                          {getTemplateTypeLabel(value as EmailTemplateType)}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  موضوع
                </label>
                <input
                  type="text"
                  name="subject"
                  defaultValue={selectedTemplate?.subject || ''}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  بدنه HTML
                </label>
                <textarea
                  name="htmlBody"
                  rows={10}
                  defaultValue={selectedTemplate?.htmlBody || ''}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="محتوای HTML ایمیل..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  بدنه متنی (اختیاری)
                </label>
                <textarea
                  name="textBody"
                  rows={5}
                  defaultValue={selectedTemplate?.textBody || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="نسخه متنی ایمیل..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  توضیحات (اختیاری)
                </label>
                <textarea
                  name="description"
                  rows={2}
                  defaultValue={selectedTemplate?.description || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="توضیحات این قالب..."
                />
              </div>

              <div className="flex items-center space-x-reverse space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    value="true"
                    defaultChecked={selectedTemplate?.isActive ?? true}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="mr-2 text-sm text-gray-700">فعال</span>
                </label>

                {isCreating && (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isDefault"
                      value="true"
                      defaultChecked={selectedTemplate?.isDefault ?? false}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="mr-2 text-sm text-gray-700">تنظیم به عنوان پیش‌فرض</span>
                  </label>
                )}
              </div>

              <div className="flex justify-end space-x-reverse space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreating(false);
                    setIsEditing(false);
                    setSelectedTemplate(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  لغو
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  onClick={(e) => {
                    console.log('Submit button clicked, isCreating:', isCreating, 'isEditing:', isEditing);
                    console.log('Mutation states - isPending:', createMutation.isPending, updateMutation.isPending);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'در حال ذخیره...'
                    : isCreating
                    ? 'ایجاد قالب'
                    : 'ذخیره قالب'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={confirm}
        onClose={cancel}
        title={modalConfig?.title || ''}
        message={modalConfig?.message || ''}
        confirmText={modalConfig?.confirmText || 'تایید'}
        cancelText={modalConfig?.cancelText || 'لغو'}
        type={modalConfig?.type || 'warning'}
      />
    </div>
  );
};

export default AdminEmailTemplates; 